<template>
  <div
    class="document-card-wrapper"
    :class="{ 'drop-zone': isOver, 'is-dragged': isDragging }"
    :draggable="isDragActivated"
    @dragstart="onDragStart"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
    @dragend="onDragEnd"
  >
    <div class="document-card-details" @click="openReferenceDialog">
      <div class="image-header">
        <BbIcon v-if="isDragActivated" size="small" name="burger" class="drag-handle" />
        <div class="image-container">
          <BaseImage class="reference-thumb" :src="firstPageUrl" alt="thumb" />
        </div>
      </div>
      <div class="reference-details">
        <div class="reference-title">{{ reference.name }}</div>
        <div class="document-details">
          <span class="bullet">•</span>
          <span>{{ documentType }}</span>
        </div>
      </div>
    </div>
    <div class="document-card-actions">
      <BbBtn class="collection-button" size="small" type="secondary" @click="handleAddToCollection">
        <BbIcon size="small" name="collection" />
        <TextBody tag="span" i18n-key="collections.addToCollection" weight="medium" />
      </BbBtn>
      <BbBtn size="small" type="secondary" @click="openReferenceDialog">
        <BbIcon size="small" name="slides" />
        <TextBody tag="span" i18n-key="common.showDetails" weight="medium" />
      </BbBtn>
      <div>
        <CollectionButton
          v-if="!isReadOnly"
          class="favorite-button"
          :document="reference"
          :collection-id="collectionId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

import { COLLECTIONS } from '~/constants/collections';
import { CURRENT_DIALOG } from '~/constants/conversationModels';

import useModals from '~/composables/useModals';
import useDocuments from '~/composables/useDocuments';
import TextBody from '~/components/common/TextBody.vue';
import BaseImage from '~/components/common/BaseImage.vue';
import CollectionButton from '~/components/common/CollectionButton';
import useConversations from '~/composables/useConversations';
import validateUrl from '~/utils/url';

export default {
  components: { TextBody, CollectionButton, BaseImage },
  props: {
    documentType: {
      type: String,
      default: 'PDF',
    },
    index: {
      type: Number,
      default: 0,
    },
    reference: {
      type: [Object, String],
      default: () => {},
    },
    collectionId: {
      type: String,
      default: COLLECTIONS.favorites,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { conversationScope, setConversationScope } = useConversations();
    const { setCurrentDocumentsDialog, setAddToCollectionPopup } = useModals();
    const { setSelectedDocument } = useDocuments();
    const isOver = ref(false);
    const isDragging = ref(false);
    const firstPageUrl = computed(() =>
      validateUrl(props.reference.pages?.[1]?.small) ? props.reference.pages?.[1]?.small : '',
    );
    // activate drag functionality only for favorites at the moment
    const isDragActivated = computed(() => props.collectionId === COLLECTIONS.favorites);
    const handleDragStart = (event, index) => emit('handleDragStart', event, index);
    const handleDragEnd = () => emit('handleDragEnd');

    const openReferenceDialog = () => {
      // avoid retrieving metadata as we already did for favorites documents
      if (props.collectionId === COLLECTIONS.favorites) {
        setCurrentDocumentsDialog(CURRENT_DIALOG.document_panel);
      } else {
        // need this to keep the logic as in DocumentPanelDialog for correct functionalities there
        const breadcrumb = [...conversationScope.value.breadcrumb, { title: ` ${props.reference.name} ` }];
        setConversationScope({ breadcrumb });
      }
      setSelectedDocument(props.reference, true);
    };

    const onDragStart = (event) => {
      isDragging.value = true;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('application/x-vue-draggable', props.index);
    };

    const onDrop = (event) => {
      const fromIndex = parseInt(event.dataTransfer.getData('application/x-vue-draggable'));
      const toIndex = props.index;
      isOver.value = false;
      emit('moveDocument', fromIndex, toIndex);
    };

    const onDragEnd = (event) => {
      event.target.style.opacity = '';
      isDragging.value = false;
      isOver.value = false;
    };

    const onDragLeave = () => {
      isOver.value = false;
    };

    const onDragOver = () => {
      isOver.value = true;
    };

    const handleAddToCollection = () => setAddToCollectionPopup({ open: true, document: props.reference });

    return {
      firstPageUrl,
      isDragging,
      isDragActivated,
      isOver,
      handleAddToCollection,
      handleDragEnd,
      handleDragStart,
      openReferenceDialog,
      onDragLeave,
      onDragOver,
      onDragStart,
      onDrop,
      onDragEnd,
    };
  },
};
</script>

<style scoped lang="scss">
.is-dragged {
  opacity: 0.1;
}

.drop-zone {
  border-bottom: 2px solid $bb-primary-3; /* Visual cue for droppable area */
}

.document-card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
  background: $bb-neutral-0;
  padding: $bb-spacing-3;
  gap: $bb-spacing-4;

  .document-card-details {
    display: flex;
    gap: $bb-spacing-3;
    align-items: flex-start;

    .image-header:hover .drag-handle {
      visibility: visible;
    }

    .image-header {
      .drag-handle {
        cursor: grab;
        display: none;
      }

      .drag-handle:active {
        cursor: grabbing;
      }

      display: flex;
      align-items: center;

      .index {
        @include bb-regular-text-styles('xs');
        text-align: center;
        width: 18px;
      }

      .image-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border-radius: $bb-spacing-1;
        background-color: $bb-neutral-5;
        min-width: 80px;
        max-width: 80px;
        height: 80px;
        gap: $bb-spacing-1;

        img {
          max-width: 76px;
          max-height: 78px;
          align-self: center;
        }
      }
    }
  }

  .document-card-actions {
    display: flex;
    gap: $bb-spacing-2;
    width: 100%;

    button {
      flex: 1;
    }

    :deep(.v-btn__content) {
      gap: $bb-spacing-1;
    }

    .collection-button {
      background: $bb-neutral-6;
      border: none !important;
    }
  }

  .reference-details {
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;

    .reference-title {
      @include bb-medium-text-styles('lg');
      line-height: $bb-spacing-5;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      max-height: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .document-details {
      display: flex;

      .bullet {
        margin: 0 $bb-spacing-1;
      }

      span {
        @include bb-regular-text-styles('md');
        color: $bb-neutral-3;
        letter-spacing: 0.1px;
      }
    }
  }
}
</style>
